import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';

import { schemas } from '@recurrency/core-api-schema';
import { ErpOrderStatus } from '@recurrency/core-api-schema/dist/common/enums';
import { SalesQuoteDTO } from '@recurrency/core-api-schema/dist/salesQuotes/getSalesQuotes';
import { SearchIndexName } from '@recurrency/core-api-schema/dist/searchIndex/common';
import { Radio } from 'antd';
import { ColumnType } from 'antd/lib/table';

import { AsyncSelect, AsyncSelectVariant, tableFilterSelectClassName } from 'components/AsyncSelect';
import { SearchSelect } from 'components/AsyncSelect/SearchSelect';
import { useLocationsSelectProps } from 'components/AsyncSelect/useAsyncSelectProps';
import { AsyncTable } from 'components/AsyncTable';
import { useCoreApiTableProps } from 'components/AsyncTable/useAsyncTableProps';
import { DateRangePicker } from 'components/DatePicker';
import { FilterBarBox } from 'components/FilterBarBox';
import { FlexSpacer } from 'components/FlexSpacer';
import { ButtonLink } from 'components/Links';
import { RadioGroup } from 'components/Radio';
import { ResultCount } from 'components/ResultCount';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { optArrFromVal } from 'utils/array';
import { truthy } from 'utils/boolean';
import { DateFilter, formatMomentDateToSqlDate, getDefaultDateFilter } from 'utils/date';
import { filterCostAndGM } from 'utils/filterCostAndGM';
import { splitIdNameStr, formatShipTo } from 'utils/formatting';
import { routes } from 'utils/routes';
import {
  sortableNumberColumn,
  sortableDateColumn,
  sortableDollarColumn,
  sortablePercentColumn,
  sortableIdColumn,
  erpStatusColumn,
} from 'utils/tables';
import { isTenantErpTypeP21 } from 'utils/tenants';

export const SalesQuotesTable = ({ customerId, companyId }: { customerId?: string; companyId?: string }) => {
  const { activeTenant } = useGlobalApp();
  const isErpTypeP21 = isTenantErpTypeP21(activeTenant.erpType);

  const columns: ColumnType<SalesQuoteDTO>[] = [
    sortableIdColumn({
      title: 'Quote No',
      dataIndex: 'salesQuoteId',
      render: (salesQuoteId: string) => <Link to={routes.orders.quoteDetails(salesQuoteId)}>{salesQuoteId}</Link>,
      sorter: true,
    }),
    erpStatusColumn(),
    sortableDateColumn({
      title: 'Quote Date',
      dataIndex: 'quoteDate',
      sorter: true,
      defaultSortOrder: 'descend',
    }),
    sortableDateColumn({
      title: 'Due Date',
      dataIndex: 'dueDate',
      sorter: true,
      defaultSortOrder: 'descend',
    }),
    sortableIdColumn({
      title: 'PO No',
      dataIndex: 'customerPORef',
      sorter: true,
    }),
    isErpTypeP21
      ? sortableIdColumn({
          title: 'Location',
          dataIndex: 'locationId',
          render: (_, record: SalesQuoteDTO) => `${record.locationId}: ${record.locationName}`,
          sorter: true,
        })
      : undefined,
    sortableIdColumn({
      title: 'Ship To',
      dataIndex: 'shipToId',
      render: (_, record: SalesQuoteDTO) => formatShipTo(record.shipToId, record.shipToName),
      sorter: true,
    }),
    sortableDollarColumn({
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      sorter: true,
    }),
    sortableDollarColumn({
      title: 'GM',
      dataIndex: 'grossMargin',
      sorter: true,
    }),
    sortablePercentColumn({
      title: 'GM%',
      dataIndex: 'grossMarginPercent',
      sorter: true,
    }),
    sortableNumberColumn({
      title: 'Line Count',
      dataIndex: 'lineCount',
      sorter: true,
    }),
    {
      render: ({ salesQuoteId }: any) => ButtonLink(routes.orders.quoteDetails(salesQuoteId)),
    },
  ]
    .filter(truthy)
    .filter(filterCostAndGM);

  const [status, setStatus] = useState<ErpOrderStatus>();

  const locationsSelectProps = useLocationsSelectProps({ companyIds: optArrFromVal(companyId) });
  const [filteredLocationId, setFilteredLocationId] = useState<string>();

  const [filteredShipToId, setFilteredShipToId] = useState<string>();
  const [filteredDate, setFilteredDate] = useState<DateFilter>(getDefaultDateFilter);

  const tableProps = useCoreApiTableProps({
    schema: schemas.salesQuotes.getSalesQuotes,
    queryParams: {
      filter: {
        status,
        customerId,
        locationId: filteredLocationId,
        shipToId: filteredShipToId,
        companyIds: companyId ? [companyId] : undefined,
        quoteDateFrom: formatMomentDateToSqlDate(filteredDate.from),
        quoteDateTo: formatMomentDateToSqlDate(filteredDate.to),
      },
    },
  });

  const { setPage } = tableProps;
  useEffect(() => {
    setPage(1);
  }, [customerId, filteredLocationId, filteredShipToId, status, setPage]);

  return (
    <>
      <FilterBarBox>
        <RadioGroup value={status} onChange={({ target: { value } }) => setStatus(value)}>
          <Radio.Button value={undefined}>All</Radio.Button>
          <Radio.Button value={ErpOrderStatus.Open}>Open</Radio.Button>
          <Radio.Button value={ErpOrderStatus.Completed}>Completed</Radio.Button>
        </RadioGroup>
        <AsyncSelect
          className={tableFilterSelectClassName}
          selectProps={locationsSelectProps}
          entityPlural="locations"
          variant={AsyncSelectVariant.Filter}
          onSelect={(_, option) => {
            tableProps.setPage(1);
            setFilteredLocationId(splitIdNameStr(option.value).foreignId);
          }}
          onClear={() => setFilteredLocationId(undefined)}
          allowClear
          size="small"
        />
        <SearchSelect
          searchParams={{ searchIndex: SearchIndexName.ShipTos, selectParams: { customerId, companyId } }}
          className={tableFilterSelectClassName}
          entityPlural="ship tos"
          variant={AsyncSelectVariant.Filter}
          onSelect={(_, option) => {
            tableProps.setPage(1);
            setFilteredShipToId(splitIdNameStr(option.value).foreignId);
          }}
          onClear={() => setFilteredShipToId(undefined)}
          allowClear
          size="small"
        />
        <FlexSpacer />
        <DateRangePicker
          value={[filteredDate.from, filteredDate.to]}
          onChange={(values) => setFilteredDate(values ? { from: values[0], to: values[1] } : getDefaultDateFilter())}
          format="MM/DD/YYYY"
        />
        <ResultCount count={tableProps.totalCount} />
      </FilterBarBox>
      <AsyncTable tableProps={tableProps} columns={columns} />
    </>
  );
};
